import React, { useEffect } from 'react';
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface ToastProps {
    id: string;
    type: ToastType;
    message: string;
    duration?: number;
    onDismiss: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ id, type, message, duration = 5000, onDismiss }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onDismiss(id);
        }, duration);

        return () => clearTimeout(timer);
    }, [id, duration, onDismiss]);

    const icons = {
        info: Info,
        success: CheckCircle2,
        warning: AlertTriangle,
        error: AlertCircle
    };

    const styles = {
        info: "bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300",
        success: "bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-300",
        warning: "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-300",
        error: "bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-900/30 dark:border-rose-800 dark:text-rose-300"
    };

    const Icon = icons[type];

    return (
        <div className={cn(
            "flex items-start gap-3 w-full max-w-sm p-4 rounded-xl border shadow-lg transition-all animate-in slide-in-from-right-full duration-300 mb-3 backdrop-blur-sm",
            styles[type]
        )}>
            <Icon className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm font-bold leading-relaxed whitespace-pre-wrap break-words">
                {message}
            </div>
            <button
                onClick={() => onDismiss(id)}
                className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition-colors shrink-0"
            >
                <X className="h-4 w-4 opacity-60" />
            </button>
        </div>
    );
};

export default Toast;
